import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import ChevronLeftIcon from "@redotech/redo-icons/chevron-left.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import LinkExternal02Icon from "@redotech/redo-icons/link-external-02.svg";
import { RedoConciergeRpcClientContext } from "@redotech/redo-merchant-app-common/concierge-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { input } from "@redotech/ui/form";
import { plainDateToCurrentDate } from "@redotech/util/temporal";
import { useMutation } from "@tanstack/react-query";
import { memo, ReactNode, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { KnowledgeCaptureContext } from "../../context/knowledge-capture-context";
import { ACTIVE_CONVERSATION_QUERY_PARAMETER } from "../../query-parameters";

/**
 * Parse an ISO timestamp string (e.g., "2026-03-01T00:00:00Z") to a Temporal.PlainDate.
 * Temporal.PlainDate.from() only accepts YYYY-MM-DD format, so we extract the date portion.
 */
function parseIsoToPlainDate(isoString: string): Temporal.PlainDate {
  return Temporal.PlainDate.from(isoString.substring(0, 10));
}

const scenarioInput = input<string>();
const responseInput = input<string>();

function TitleIcon() {
  return (
    <div
      style={{
        width: "48px",
        height: "48px",
        backgroundColor: "var(--redo-colors-orange-50)",
        borderRadius: "9999px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "-12px",
      }}
    >
      <AiStarIcon
        style={{
          width: "24px",
          height: "24px",
          color: "var(--redo-colors-orange-dark-500)",
        }}
      />
    </div>
  );
}

interface ConversationLinkChipProps {
  onClick: () => void;
}

const ConversationLinkChip = memo(function ConversationLinkChip({
  onClick,
}: ConversationLinkChipProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline",
        background: "none",
        border: "none",
        padding: 0,
        margin: 0,
        font: "inherit",
        color: "var(--redo-colors-gray-500)",
        textDecoration: "underline",
        cursor: "pointer",
        verticalAlign: "baseline",
      }}
      type="button"
    >
      this recent conversation
      <LinkExternal02Icon
        style={{
          display: "inline",
          width: "12px",
          height: "12px",
          marginLeft: "2px",
          verticalAlign: "middle",
        }}
      />
    </button>
  );
});

function buildSubtitle(
  hasConversation: boolean,
  onViewConversation: () => void,
): ReactNode {
  if (!hasConversation) {
    return "The AI handed off to a human. Add this knowledge, and your AI will handle this scenario going forward.";
  }

  return (
    <>
      In <ConversationLinkChip onClick={onViewConversation} />, the AI didn't
      handle an issue. Add this knowledge, and your AI will handle it correctly
      going forward.
    </>
  );
}

interface KnowledgeCaptureModalProps {
  /** Optional: When provided, overrides context isOpen */
  isOpen?: boolean;
  /** Optional: Callback to close just the modal (keeps toast visible) */
  onCloseModal?: () => void;
}

export const KnowledgeCaptureModal = memo(function KnowledgeCaptureModal({
  isOpen: isOpenProp,
  onCloseModal,
}: KnowledgeCaptureModalProps) {
  const {
    isOpen: isOpenFromContext,
    conversationId,
    suggestions,
    hideKnowledgeCapture,
    editedValues,
    setEditedValues,
    currentIndex,
    setCurrentIndex,
    resolvedSuggestionIndices,
    setResolvedSuggestionIndices,
  } = useRequiredContext(KnowledgeCaptureContext);

  // Use prop if provided, otherwise fall back to context
  const isOpen = isOpenProp ?? isOpenFromContext;
  const conciergeRpcClient = useRequiredContext(RedoConciergeRpcClientContext);
  const team = useRequiredContext(TeamContext);

  const navigate = useNavigate();
  const location = useLocation();

  const handleViewConversation = useHandler(() => {
    if (!conversationId) {
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    searchParams.set(ACTIVE_CONVERSATION_QUERY_PARAMETER, conversationId);
    // Close modal but keep toast visible so user can reopen
    if (onCloseModal) {
      onCloseModal();
    } else {
      hideKnowledgeCapture();
    }
    navigate(`${location.pathname}?${searchParams}`);
  });

  const allSuggestions = suggestions?.suggestions ?? [];
  const pendingSuggestions = allSuggestions
    .map((suggestion, index) => ({ suggestion, originalIndex: index }))
    .filter(
      ({ originalIndex }) => !resolvedSuggestionIndices.has(originalIndex),
    );

  const totalPending = pendingSuggestions.length;
  const hasMultipleSuggestions = totalPending > 1;

  const safeIndex = Math.min(currentIndex, totalPending - 1);
  const currentEntry = pendingSuggestions[safeIndex >= 0 ? safeIndex : 0];
  const currentSuggestion = currentEntry?.suggestion;
  const currentOriginalIndex = currentEntry?.originalIndex;

  const SCENARIO_LABEL = "Scenario";
  const RESPONSE_LABEL = "AI response";

  const currentEdited =
    currentOriginalIndex !== undefined
      ? editedValues[currentOriginalIndex]
      : undefined;
  const displayScenario =
    currentEdited?.scenario ?? currentSuggestion?.whenToUse ?? "";
  const displayResponse =
    currentEdited?.response ?? currentSuggestion?.knowledge ?? "";

  // Parse expiresAt from suggestion (string) to Temporal.PlainDate
  const suggestionExpiresAt = currentSuggestion?.expiresAt
    ? parseIsoToPlainDate(currentSuggestion.expiresAt)
    : undefined;
  const displayExpiresAt = currentEdited?.expiresAt ?? suggestionExpiresAt;

  const scenarioInputState = useInput(scenarioInput, displayScenario);
  const responseInputState = useInput(responseInput, displayResponse);
  const [expiresAtState, setExpiresAtState] = useState<
    Temporal.PlainDate | undefined
  >(displayExpiresAt);

  const saveCurrentEditsAndNavigate = useHandler((newIndex: number) => {
    if (currentOriginalIndex === undefined) return;

    const savedEdits = [...editedValues];
    savedEdits[currentOriginalIndex] = {
      scenario: scenarioInputState.value,
      response: responseInputState.value,
      expiresAt: expiresAtState,
    };
    setEditedValues(savedEdits);
    setCurrentIndex(newIndex);

    const { suggestion, originalIndex } = pendingSuggestions[newIndex] ?? {};
    if (suggestion) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const edited = savedEdits[originalIndex];
      scenarioInputState.setValue(edited?.scenario ?? suggestion.whenToUse);
      responseInputState.setValue(edited?.response ?? suggestion.knowledge);
      const newExpiresAt =
        edited?.expiresAt ??
        (suggestion.expiresAt
          ? parseIsoToPlainDate(suggestion.expiresAt)
          : undefined);
      setExpiresAtState(newExpiresAt);
    }
  });

  const handlePrevious = useHandler(() => {
    if (currentIndex > 0) {
      saveCurrentEditsAndNavigate(currentIndex - 1);
    }
  });

  const handleNext = useHandler(() => {
    if (currentIndex < totalPending - 1) {
      saveCurrentEditsAndNavigate(currentIndex + 1);
    }
  });

  const markCurrentAsResolvedAndAdvance = useHandler(
    (newResolvedIndices: Set<number>) => {
      if (newResolvedIndices.size >= allSuggestions.length) {
        hideKnowledgeCapture();
        return;
      }

      const newPending = allSuggestions
        .map((suggestion, index) => ({ suggestion, originalIndex: index }))
        .filter(({ originalIndex }) => !newResolvedIndices.has(originalIndex));

      const newCurrentIndex = Math.min(currentIndex, newPending.length - 1);
      const { suggestion, originalIndex } = newPending[newCurrentIndex] ?? {};

      setResolvedSuggestionIndices(newResolvedIndices);
      setCurrentIndex(newCurrentIndex);

      if (suggestion) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const edited = editedValues[originalIndex];
        scenarioInputState.setValue(edited?.scenario ?? suggestion.whenToUse);
        responseInputState.setValue(edited?.response ?? suggestion.knowledge);
        const newExpiresAt =
          edited?.expiresAt ??
          (suggestion.expiresAt
            ? parseIsoToPlainDate(suggestion.expiresAt)
            : undefined);
        setExpiresAtState(newExpiresAt);
      }
    },
  );

  const approveKnowledgeMutation = useMutation({
    mutationFn: async (params: {
      knowledgeId: string;
      whenToUse: string;
      title: string;
      knowledge: string;
      expiresAt?: Date | null;
      originalIndex: number;
    }) => {
      await conciergeRpcClient.updateConciergeKnowledge({
        _id: params.knowledgeId,
        whenToUse: params.whenToUse,
        title: params.title,
        knowledge: params.knowledge,
        enabled: true,
        expiresAt: params.expiresAt,
      });
      return { originalIndex: params.originalIndex };
    },
    onSuccess: ({ originalIndex }) => {
      toast("Knowledge added successfully", { variant: "success" });
      const newResolvedIndices = new Set(resolvedSuggestionIndices);
      newResolvedIndices.add(originalIndex);
      markCurrentAsResolvedAndAdvance(newResolvedIndices);
    },
    onError: () => {
      toast("Failed to add knowledge", { variant: "error" });
    },
  });

  const handleAddKnowledge = useHandler(() => {
    if (
      !conciergeRpcClient ||
      !currentSuggestion ||
      currentOriginalIndex === undefined
    ) {
      return;
    }

    const suggestionScenario = scenarioInputState.value.trim();
    const suggestedAiResponse = responseInputState.value.trim();
    if (!suggestionScenario || !suggestedAiResponse) {
      toast(`Please fill in both ${SCENARIO_LABEL} and ${RESPONSE_LABEL}`, {
        variant: "error",
      });
      return;
    }

    amplitude.logEvent("suggested-knowledge/add-clicked", {
      knowledgeId: currentSuggestion.knowledgeId,
      knowledgeTitle: currentSuggestion.title,
      teamId: team._id,
      conversationId,
      source: "modal",
    });

    approveKnowledgeMutation.mutate({
      knowledgeId: currentSuggestion.knowledgeId,
      whenToUse: scenarioInputState.value,
      title: currentSuggestion.title,
      knowledge: responseInputState.value,
      expiresAt: expiresAtState ? plainDateToCurrentDate(expiresAtState) : null,
      originalIndex: currentOriginalIndex,
    });
  });

  const handleSkipCurrent = useHandler(() => {
    if (currentOriginalIndex === undefined || !currentSuggestion) return;

    amplitude.logEvent("suggested-knowledge/skip-clicked", {
      knowledgeId: currentSuggestion.knowledgeId,
      knowledgeTitle: currentSuggestion.title,
      teamId: team._id,
      conversationId,
      source: "modal",
    });

    const newResolvedIndices = new Set(resolvedSuggestionIndices);
    newResolvedIndices.add(currentOriginalIndex);
    markCurrentAsResolvedAndAdvance(newResolvedIndices);
  });

  const handleCloseModal = useHandler(() => {
    // Save current edits before closing so they persist when reopened
    if (currentOriginalIndex !== undefined) {
      setEditedValues((prev) => {
        const next = [...prev];
        next[currentOriginalIndex] = {
          scenario: scenarioInputState.value,
          response: responseInputState.value,
          expiresAt: expiresAtState,
        };
        return next;
      });
    }

    // Use onCloseModal if provided (keeps toast visible), otherwise dismiss everything
    if (onCloseModal) {
      onCloseModal();
    } else {
      hideKnowledgeCapture();
    }
  });

  const handleSetExpiresAt = useHandler((date: Temporal.PlainDate) => {
    setExpiresAtState(date);
  });

  const handleExpirationModeChange = useHandler(
    (mode: "forever" | "select-date") => {
      if (mode === "forever") {
        setExpiresAtState(undefined);
      } else if (!expiresAtState) {
        // Default to 30 days from now when switching to select-date
        setExpiresAtState(Temporal.Now.plainDateISO().add({ days: 30 }));
      }
    },
  );

  if (!isOpen || !currentSuggestion) {
    return null;
  }

  const hasConversation = !!conversationId;

  const expirationMode = expiresAtState ? "select-date" : "forever";

  const validUntilFooterContent = (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="tertiary">
        Knowledge active until
      </Text>
      <div style={{ width: "180px" }}>
        <RedoButtonTabGroup
          disabled={approveKnowledgeMutation.isPending}
          onChange={handleExpirationModeChange}
          options={[
            { value: "forever" as const, el: "Forever" },
            { value: "select-date" as const, el: "Select date" },
          ]}
          size="xs"
          value={expirationMode}
        />
      </div>
      {expiresAtState && (
        <div style={{ width: "180px" }}>
          <RedoSingleDatePicker
            buttonSize="sm"
            date={expiresAtState}
            minDate={Temporal.Now.plainDateISO()}
            setDate={handleSetExpiresAt}
          />
        </div>
      )}
    </Flex>
  );

  return (
    <RedoModal
      additionalFooterContent={validUntilFooterContent}
      footerLayout="column"
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={handleCloseModal}
      primaryButton={{
        text: "Add knowledge",
        onClick: handleAddKnowledge,
        pending: approveKnowledgeMutation.isPending,
        size: "sm",
      }}
      secondaryButton={{ text: "Skip", onClick: handleSkipCurrent, size: "sm" }}
      subtitle={buildSubtitle(hasConversation, handleViewConversation)}
      theme="brand"
      title={currentSuggestion.title}
      TitleIcon={TitleIcon}
    >
      <Flex dir="column" gap="lg" w="full">
        <RedoTextInput
          fullWidth
          label={SCENARIO_LABEL}
          setValue={scenarioInputState.setValue}
          state={approveKnowledgeMutation.isPending ? "disabled" : "default"}
          value={scenarioInputState.value}
        />

        <RedoTextareaInputField
          containerHeight="15vh"
          disabled={approveKnowledgeMutation.isPending}
          fullWidth
          label={RESPONSE_LABEL}
          setValue={responseInputState.setValue}
          value={responseInputState.value}
        />

        {hasMultipleSuggestions && (
          <Flex align="center" gap="sm" justify="center" w="full">
            <RedoButton
              disabled={currentIndex === 0}
              hierarchy="tertiary"
              IconLeading={ChevronLeftIcon}
              onClick={handlePrevious}
              size="sm"
            />
            <Text fontSize="sm" textColor="tertiary">
              {safeIndex + 1} of {totalPending}
            </Text>
            <RedoButton
              disabled={currentIndex === totalPending - 1}
              hierarchy="tertiary"
              IconLeading={ChevronRightIcon}
              onClick={handleNext}
              size="sm"
            />
          </Flex>
        )}
      </Flex>
    </RedoModal>
  );
});
